/**
 * DeliverySLA constants.
 *
 * Shared, presentation-agnostic constants for the multi-seller Delivery SLA UI.
 * These are consumed by both the mapping layer (`mapDeliverySLA`) and the
 * presentation layer, so keep them free of any React / redux imports.
 */

/** UI / Amplitude priceTypeTags keys (best → priceTypeTags.best, etc.). */
export const PRICE_TYPE = {
  BEST: 'best',
  FASTEST: 'fastest'
} as const;

/**
 * scopedView API priceType values. Mapped to PRICE_TYPE for UI / Amplitude
 * `priceTypeTags` (`best` / `fastest`) on PDP + wishlist.
 */
export const SCOPED_VIEW_PRICE_TYPE = {
  BEST_PRICE: 'BEST_PRICE',
  BEST_SPEED: 'BEST_SPEED',
  /** One option that is both cheapest and fastest — nothing left to choose. */
  BEST_SPEED_PRICE: 'BEST_SPEED_PRICE'
} as const;

/**
 * True when the priceType says the option wins on price *and* speed
 * (BEST_SPEED_PRICE / BEST_PRICE_SPEED), i.e. there is no trade-off to present.
 */
export const isBestPriceAndSpeedType = (priceType?: string): boolean => {
  const key = String(priceType || '').toUpperCase();
  return key.includes('PRICE') && key.includes('SPEED');
};

/**
 * True when the priceType is one the badge speaks for: BEST_PRICE / BEST_SPEED
 * (with the legacy `best` / `fastest` spellings `normalizePriceType` accepts).
 *
 * The seller list returns a row per seller, most of them carrying no priceType
 * at all — those rows get no badge rather than the "BEST PRICE" default.
 */
export const hasBadgePriceType = (priceType?: string): boolean => {
  const key = String(priceType || '')
    .trim()
    .toUpperCase()
    .replace(/-/g, '_');
  return (
    key === SCOPED_VIEW_PRICE_TYPE.BEST_PRICE ||
    key === SCOPED_VIEW_PRICE_TYPE.BEST_SPEED ||
    key === 'BEST' ||
    key === 'FASTEST'
  );
};

/** Delivery methods returned per product detail. */
export const DELIVERY_METHOD = {
  STANDARD: 'STANDARD',
  RUSH: 'RUSH',
  PRIORITY: 'PRIORITY'
} as const;

/**
 * Default badge copy per priceType (fallback when the flag config's
 * priceTypeTags are absent). `best` -> "BEST PRICE", `fastest` -> "FASTER".
 */
export const SLA_BADGE_LABEL = {
  BEST: 'BEST PRICE',
  FASTEST: 'FASTER'
} as const;

/**
 * How many seller cards the cart size/qty sheet shows. The deliveryChoices API
 * marks at most two options `defaultDisplay: true`; the rest are reached through
 * "Other sellers available". Kept here so both the msite sheet and the desktop
 * popup cap at the same number.
 */
export const B2C_MAX_DEFAULT_SELLERS = 2;

/** Where the SLA list is being rendered. Drives small copy/badge differences. */
export const SLA_CONTEXT = {
  PDP: 'pdp',
  CART: 'cart'
} as const;

/**
 * Which screen the SLA card is mounted on. Distinct from SLA_CONTEXT, which only
 * says how the cards read (cart suffixes "/ pc") — two different screens (the PDP
 * card and the PDP size half-card) share one context but are separate screen
 * views for analytics.
 */
export const SLA_SURFACE = {
  PDP: 'pdp',
  SIZE_HALF_CARD: 'half card',
  ALL_SELLERS: 'all sellers',
  BAG_SHEET: 'bag',
  WISHLIST_SHEET: 'wishlist'
} as const;

export type SlaSurface = typeof SLA_SURFACE[keyof typeof SLA_SURFACE];

interface SlaSurfaceScreenNames {
  screen_name: string;
  screen_source: string;
  previous_screen_name: string;
}

/**
 * screen_name / screen_source / previous_screen_name for the
 * "Delivery And Return Details Viewed" event, per surface.
 *
 * The PDP rows reproduce what the multiseller emitters already send (msite takes
 * the luxe PDP screen name, desktop the literal "PDP screen"), so the B2C cohort
 * reports against the same names. Bag / wishlist wording matches
 * `ProductCardDesktop`'s pageType mapping.
 */
export const getSlaSurfaceScreenNames = (
  surface: SlaSurface = SLA_SURFACE.PDP,
  {
    luxePdpScreenName = '',
    isDesktop = false,
    /** For ALL_SELLERS: the surface the list was opened from. */
    openedFrom = SLA_SURFACE.PDP
  }: { luxePdpScreenName?: string; isDesktop?: boolean; openedFrom?: SlaSurface } = {}
): SlaSurfaceScreenNames => {
  const sourceOf = (from: SlaSurface): string => {
    if (from === SLA_SURFACE.BAG_SHEET) return 'bag screen';
    if (from === SLA_SURFACE.WISHLIST_SHEET) return 'wishlist screen';
    return 'pdp screen';
  };
  if (surface === SLA_SURFACE.ALL_SELLERS) {
    return {
      screen_name: 'all sellers screen',
      screen_source: sourceOf(openedFrom),
      previous_screen_name: sourceOf(openedFrom)
    };
  }
  if (surface === SLA_SURFACE.PDP) {
    return {
      screen_name: isDesktop ? 'PDP screen' : luxePdpScreenName,
      screen_source: 'pdp screen',
      previous_screen_name: 'pdp screen'
    };
  }
  return {
    screen_name: 'size selection halfcard screen',
    screen_source: sourceOf(surface),
    previous_screen_name: sourceOf(surface)
  };
};

export type PriceType = (typeof PRICE_TYPE)[keyof typeof PRICE_TYPE];
export type DeliveryMethod = (typeof DELIVERY_METHOD)[keyof typeof DELIVERY_METHOD];
export type SlaContext = (typeof SLA_CONTEXT)[keyof typeof SLA_CONTEXT];

/**
 * multiSellerSlaConfig note by card count:
 * 1 → oneSlaDeliveryNote, 2+ → twoSlaDeliveryNote.
 */
export const pickSlaDeliveryNote = (
  config: {
    oneSlaDeliveryNote?: string;
    twoSlaDeliveryNote?: string;
    deliveryNote?: string;
    /** B2C names the same note `deliverySlaNote`, with no per-count variants. */
    deliverySlaNote?: string;
  } = {},
  slaCardCount = 0
): string => {
  if (Number(slaCardCount) > 1) {
    return config.twoSlaDeliveryNote || config.deliverySlaNote || '';
  }
  return config.oneSlaDeliveryNote || config.deliveryNote || config.deliverySlaNote || '';
};

/**
 * Map scopedView / legacy priceType → UI key used with Amplitude priceTypeTags.
 * BEST_PRICE → best, BEST_SPEED → fastest (also accepts legacy best/fastest).
 */
export const normalizePriceType = (priceType?: string): PriceType => {
  const key = String(priceType || '')
    .trim()
    .toUpperCase()
    .replace(/-/g, '_');
  if (
    key === SCOPED_VIEW_PRICE_TYPE.BEST_SPEED ||
    key === 'FASTEST' ||
    key === PRICE_TYPE.FASTEST.toUpperCase()
  ) {
    return PRICE_TYPE.FASTEST;
  }
  return PRICE_TYPE.BEST;
};

/**
 * Catalogue seller for a size, off the wishlist listing payload
 * (`variantOptions[].sellerName`, with the product-level `sellerName` as
 * fallback).
 *
 * Only for the no-location wishlist state: with no pincode there is no
 * scopedView response, so the seller cannot be read off an SLA option. Once a
 * location is set the card's own seller row takes over, since the seller can
 * differ per pincode — which is what the note beside this warns about.
 */
export const sellerNameForSize = (
  variantOptions: { code?: string; sellerName?: string }[] = [],
  sizeCode = '',
  fallbackSellerName = ''
): string => {
  const list = Array.isArray(variantOptions) ? variantOptions : [];
  const match = sizeCode ? list.find(option => option?.code === sizeCode) : undefined;
  return match?.sellerName || fallbackSellerName || '';
};
